import classNames from "classnames";
import { ReactionSelector } from "components/chat/ReactionSelector";
import { Button } from "components/DesignSystem/Button/Button";
import {
  default as DropDown,
  default as Dropdown,
} from "components/DesignSystem/Dropdown/Dropdown";
import { ArrowBendDownLeft } from "components/icons/ArrowBendDownLeft";
import { Smiley } from "components/icons/Chat/Smiley";
import { Sparkle } from "components/icons/Sparkle";
import ChatTicket from "static/images/ChatTicket.svg";
import dayjs from "dayjs";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { ShowControl } from "hooks/useShow";
import { useToast } from "hooks/useToast";
import { useDispatch } from "react-redux";
import { useGenerateResponseForMessageMutation } from "store/apis/chat";
import {
  resetGeneratingMessage,
  setGeneratingMessage,
} from "store/slices/generateChatMessage";
import { openCreateChatTicketModal } from "store/slices/ticket";
import {
  MessageUIComponentProps,
  StreamMessage,
  useChannelStateContext,
  useChatContext,
} from "stream-chat-react";
import { DefaultStreamChatGenerics } from "stream-chat-react/dist/types/types";
import { BackendError } from "types/utils/error";
import { MessageComponentProps } from "./MessageBubble";

import ConditionalToolTip from "components/design/conditionalToolTip";
import { OptionDropdown } from "components/icons/optionDropdown";
import {
  AI_CHANNEL,
  ANNOUNCEMENTS_CHANNEL,
  REQUESTED_INFO_CUSTOM_TYPE,
} from "constants/chatType";
import { DEPRECATED, PROCESSING, REQUESTED } from "constants/sensitiveData";
import { useModal } from "hooks/useModal";
import { openMessageInfoSlider } from "store/slices/channelInfoSlider";
import {
  setDeleteMessageData,
  setMessageForStatusUpdate,
} from "store/slices/chat";
import { setMessageToForward } from "store/slices/messageForward";
import { MessageResponse } from "stream-chat";
import { MoveChatDocuments } from "./MoveChatDocuments";
import { SendToEmail } from "./SendToEmail";
import { SendToSlack } from "./SendToSlack";
import { SendToWhatsapp } from "./SendToWhatsapp";

type MessageActionsProps = {
  message: MessageUIComponentProps["message"];
  sentByTheUser: boolean;
  onQuoteClick: () => void;
  reactionMenu: ShowControl;
  channelId: string;
  optionProps: MessageComponentProps;
  optionsMenu: ShowControl;
};

type OptionsProps = MessageComponentProps & {
  optionsMenu: ShowControl;
};

export const MessageActions = ({
  message,
  sentByTheUser,
  onQuoteClick,
  reactionMenu,
  channelId,
  optionProps,
  optionsMenu,
}: MessageActionsProps) => {
  const { isAdmin, canManageInternalTickets } = useRoleBasedView();
  const dispatch = useDispatch();
  const { alertToast } = useToast();
  const [generateResponse, { isLoading: generatingMessage }] =
    useGenerateResponseForMessageMutation();

  const onGenerateResponse = async () => {
    if (message?.text) {
      try {
        dispatch(
          setGeneratingMessage({ generating: true, channelId: channelId })
        );
        await generateResponse({
          message: message.text,
          channelId: channelId,
        }).unwrap();
        dispatch(resetGeneratingMessage());
      } catch (error) {
        alertToast({
          message: (error as BackendError).data?.error?.message,
        });
      }
    }
  };

  const allowGenerateResponse = isAdmin && message?.text && !sentByTheUser;

  return (
    <div
      className={classNames(
        "t-absolute t-flex t-items-center t-bg-surface t-rounded-lg t-border-neutral-10 t-border-solid t-border t-z-10 t-overflow-hidden t-p-1",
        {
          "t-pointer-events-auto t-opacity-100":
            reactionMenu.shown || optionsMenu.shown,
          "group-hover:t-opacity-100 t-opacity-0":
            !reactionMenu.shown || !optionsMenu.shown,
          "-t-top-4 t-left-4": sentByTheUser,
          "-t-top-4 t-right-4": !sentByTheUser,
        }
      )}
    >
      {allowGenerateResponse && (
        <Button
          isLoading={generatingMessage}
          disabled={generatingMessage}
          customType="ghost_icon"
          size="xsmall"
          onClick={onGenerateResponse}
        >
          <Sparkle />
        </Button>
      )}

      {canManageInternalTickets && (
        <Button
          customType="ghost_icon"
          size="xsmall"
          onClick={() =>
            dispatch(
              openCreateChatTicketModal({
                messageId: message?.id || "",
                showCreateTicketModal: true,
                channelId: channelId,
                isInternalTicket: true,
                chatContext: message?.text || "",
              })
            )
          }
        >
          <img src={ChatTicket} alt="ChatTicket" className="t-size-4" />
        </Button>
      )}

      <DropDown.Root modal={false} onOpenChange={reactionMenu.toggle}>
        <DropDown.Trigger asChild>
          <Button customType="ghost_icon" size="xsmall">
            <Smiley />
          </Button>
        </DropDown.Trigger>
        <DropDown.Portal>
          <DropDown.Content
            sideOffset={10}
            className="t-rounded-lg t-bg-surface t-drop-shadow-sidebar"
            align={sentByTheUser ? "start" : "end"}
          >
            <ReactionSelector />
          </DropDown.Content>
        </DropDown.Portal>
      </DropDown.Root>

      <div
        className={classNames(
          "t-pointer-events-none t-flex t-gap-2 t-transition-opacity group-hover:t-pointer-events-auto"
        )}
      >
        <Button customType="ghost_icon" size="xsmall" onClick={onQuoteClick}>
          <span className="t-rotate-x-180">
            <ArrowBendDownLeft />
          </span>
        </Button>
      </div>

      <Options {...optionProps} optionsMenu={optionsMenu} />
    </div>
  );
};

const Options = (props: OptionsProps) => {
  const { message, otherData, setQuotedMessage, optionsMenu } = props;
  const { client } = useChatContext();
  const { channel } = useChannelStateContext();
  const dispatch = useDispatch();
  const { alertToast, successToast } = useToast();
  const { isAdmin, isInkleSuperAdmin, canManageInternalTickets } =
    useRoleBasedView();
  const { sentByTheUser } = otherData;
  const sendToWhatsappModal = useModal();
  const sendToEmailModal = useModal();
  const moveDocumentsModal = useModal();
  const sendToSlackModal = useModal();
  const attachments = message.attachments || [];

  const [generateResponse] = useGenerateResponseForMessageMutation();

  const isMessageSentBefore10Minute =
    dayjs().diff(message?.created_at, "minutes") > 10;

  const isMessageDeletableByAdmin =
    isAdmin && !isMessageSentBefore10Minute && sentByTheUser;

  const isMessageDeletableByInkleSuperAdmin =
    isInkleSuperAdmin && !isMessageSentBefore10Minute;

  const isMessageDeletable =
    isMessageDeletableByAdmin || isMessageDeletableByInkleSuperAdmin;

  const disableWhatsappShare = Boolean(
    message.attachments && message.attachments?.length > 0 && !message.text
  );

  const isAnnouncementOrAiChannel =
    channel?.data?.custom_type === ANNOUNCEMENTS_CHANNEL ||
    channel?.data?.custom_type === AI_CHANNEL;
  const isPinned = message.pinned;

  const onQuoteClick = () => {
    const textArea = document.getElementById("message-textarea");
    if (textArea) {
      textArea?.focus();
    }
    setQuotedMessage(message);
  };

  const pinMesaageToggle = async () => {
    try {
      if (isPinned) {
        await client.unpinMessage(message!);
        successToast({ title: "Message was unpinned" });
      } else {
        await client.pinMessage(message!, null);
        successToast({ title: "Message was pinned" });
      }
    } catch (error) {
      //@ts-ignore
      alertToast({ message: error?.messages }, error as Error);
    }
  };

  type InternalMessage = MessageUIComponentProps["message"] & {
    forwarded_message?: MessageUIComponentProps["message"];
  };

  const generateAndAutofillMessage = async (message: InternalMessage) => {
    if (message.text && channel.id) {
      try {
        dispatch(
          setGeneratingMessage({ generating: true, channelId: channel.id })
        );
        await generateResponse({
          message: message.text,
          channelId: channel.id,
        }).unwrap();

        dispatch(resetGeneratingMessage());
      } catch (error) {
        alertToast(
          {
            message: (error as BackendError).data?.error?.message,
          },
          error as Error
        );
      }
    }
  };

  return (
    <>
      <Dropdown.Root modal={false} onOpenChange={optionsMenu.toggle}>
        <Dropdown.Trigger asChild>
          <Button customType="ghost_icon" size="xsmall">
            <OptionDropdown />
          </Button>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content
            className="t-min-w-48"
            sideOffset={10}
            align={sentByTheUser ? "start" : "end"}
          >
            {!isAnnouncementOrAiChannel && (
              <Dropdown.Item onSelect={onQuoteClick}>Reply</Dropdown.Item>
            )}
            {isAdmin && attachments.length > 0 && (
              <Dropdown.Item onSelect={moveDocumentsModal.open}>
                Move documents
              </Dropdown.Item>
            )}
            {canManageInternalTickets && (
              <Dropdown.Item
                onSelect={() =>
                  dispatch(
                    openCreateChatTicketModal({
                      messageId: message?.id,
                      showCreateTicketModal: true,
                      channelId: channel.id || "",
                      isInternalTicket: false,
                    })
                  )
                }
              >
                Create Ticket
              </Dropdown.Item>
            )}
            {!isAnnouncementOrAiChannel && isAdmin && (
              <Dropdown.Item onSelect={pinMesaageToggle}>
                {isPinned ? "Unpin" : "Pin"}
              </Dropdown.Item>
            )}
            {!message?.forwarded_message && isAdmin && (
              <Dropdown.Item
                onSelect={() =>
                  message?.id &&
                  dispatch(
                    setMessageToForward({
                      messageId: message?.id,
                    })
                  )
                }
              >
                Forward
              </Dropdown.Item>
            )}
            {isMessageDeletable && (
              <Dropdown.Item
                type="danger"
                onSelect={() =>
                  dispatch(
                    setDeleteMessageData({
                      message,
                      showDeleteMessageModal: true,
                      channelId: channel.id || "",
                    })
                  )
                }
              >
                Delete
              </Dropdown.Item>
            )}
            {isAdmin && (
              <>
                <Dropdown.Item
                  onSelect={sendToWhatsappModal.open}
                  disabled={disableWhatsappShare}
                >
                  <ConditionalToolTip
                    condition={
                      disableWhatsappShare &&
                      "Currently, attachment forwarding is not supported for WhatsApp. 📎"
                    }
                  >
                    Send to Whatsapp
                  </ConditionalToolTip>
                </Dropdown.Item>
                <Dropdown.Item onSelect={sendToEmailModal.open}>
                  Send to Email
                </Dropdown.Item>
                {message.custom_type === REQUESTED_INFO_CUSTOM_TYPE && (
                  <Dropdown.Item onSelect={sendToSlackModal.open}>
                    Send to Slack
                  </Dropdown.Item>
                )}
                {Boolean(message.custom_type) &&
                  (message.card_status === REQUESTED ||
                    message.card_status === PROCESSING) && (
                    <Dropdown.Item
                      onSelect={() =>
                        dispatch(
                          setMessageForStatusUpdate(message as StreamMessage)
                        )
                      }
                    >
                      Archive item
                    </Dropdown.Item>
                  )}
                {Boolean(message.custom_type) &&
                  message.card_status === DEPRECATED && (
                    <Dropdown.Item
                      onSelect={() =>
                        dispatch(setMessageForStatusUpdate(message))
                      }
                    >
                      Reopen item
                    </Dropdown.Item>
                  )}
                <Dropdown.Item
                  onSelect={() => dispatch(openMessageInfoSlider(message.id))}
                >
                  Message info
                </Dropdown.Item>
              </>
            )}
            {isAdmin && message.text && !sentByTheUser && (
              <Dropdown.Item
                onSelect={() => generateAndAutofillMessage(message)}
              >
                Generate response <Sparkle />
              </Dropdown.Item>
            )}
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>

      {sendToWhatsappModal.isOpen && (
        <SendToWhatsapp
          isOpen={sendToWhatsappModal.isOpen}
          close={sendToWhatsappModal.close}
          message={message}
        />
      )}
      {sendToEmailModal.isOpen && (
        <SendToEmail {...sendToEmailModal} message={message} />
      )}
      {sendToSlackModal.isOpen && (
        <SendToSlack {...sendToSlackModal} message={message} />
      )}
      {moveDocumentsModal.isOpen && (
        <MoveChatDocuments
          {...moveDocumentsModal}
          message={message as MessageResponse<DefaultStreamChatGenerics>}
        />
      )}
    </>
  );
};
